import { ITableState, ModifyView } from "@mirage/components/entityTable/EntityTable";
import { ShowAlert } from "@mirage/hooks/useAlertService";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { EntityBinding } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import { PredefinedEntityTableView, PredefinedEntityTableViewId } from "@shared/mirage/services/IUserPreferencesService";
import { createContext, useContext } from "react";
import { Field, RuleGroupTypeIC, RuleType } from "react-querybuilder";

const EntityTableContext = createContext({} as EntityTableContext);

export function useEntityTable() {
    return useContext(EntityTableContext);
}

export function EntityTableProvider({ children, value }: { children: React.ReactNode; value: EntityTableContext }) {
    return <EntityTableContext.Provider value={value}>{children}</EntityTableContext.Provider>;
}

export interface EntityTableContext {
    setDateFilterPopper: React.Dispatch<React.SetStateAction<DateFieldPopper>>;
    dateFilterPopper: DateFieldPopper | null;
    apiRef: React.MutableRefObject<GridApiPremium>;
    showAlert: ShowAlert;
    openAdvancedQueryBuilder: () => void;
    closeAdvancedQueryBuilder: () => void;
    closeViewOrganizeModal: () => void;
    updateAdvancedQueryBuilderQuery: (query: RuleGroupTypeIC<RuleType<string, string, any, string>, string>) => void;
    tableState: ITableState;
    selectPredefinedEntityTable: (id: PredefinedEntityTableViewId) => void;
    updateAdvancedQueryBuilderName: (name: string) => void;
    updatePredefinedView: UpdatePredefinedView;
    onOrganizeViewsSave: () => void;
    onOrganizeViewsCancel: () => void;
    onViewSave: () => void;
    onViewCancel: () => void;
    deleteTableView: () => Promise<void>;
    handleOnPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, pageIndex: number) => void;
    modifyView: ModifyView;
    experimentalQueryBuilder?: boolean;
    updatePredefinedEntityTableViewsOrder: (viewId: PredefinedEntityTableViewId, sourceIdx: number) => void;
    orderedPredefinedTableViews: PredefinedEntityTableView[];
    metadata: EntityBinding<unknown> | undefined;
    changedNames?: Record<string, string>;
}

export interface DateFieldPopper {
    open: boolean;
    elementRef: HTMLDivElement | null;
    fieldName: string;
}

export interface AdvancedQueryBuilder {
    open: boolean;
    query: RuleGroupTypeIC<RuleType<string, string, any, string>, string>;
    fields: Field[];
}

export type UpdatePredefinedView = <Key extends keyof EditablePredefinedViewFields>(field: Key, value: PredefinedEntityTableView[Key], viewId: string) => void;
export type EditablePredefinedViewFields = Pick<PredefinedEntityTableView, "name" | "color" | "isAdministratorView" | "description" | "colorChanged">;
